/**
 * The signed-in person's help state (GET /v1/workspaces/:w/help/state, docs/api/HELP.md): the
 * welcome checklist, which pages' coach-marks were shown, the newest "มีอะไรใหม่" entry seen.
 *
 * Part of the app shell (the "?" button's dot, the coach-mark host, the home page's checklist all
 * read it), so it stays tiny: one cached request per workspace through lib/swr, and optimistic
 * changes that are sent with PATCH /v1/me/help. Nothing here imports the help centre itself.
 */
import { useCallback } from 'react';
import { api } from '../api/client.js';
import type { HelpPatch, HelpState } from '../api/types.js';
import { useSession } from '../app/session.js';
import { invalidateSWR, updateSWR, useSWR } from '../lib/swr.js';
import { whatsNewSeq, whatsNewUnread } from './latest.js';

const KEY = 'help-state:';
/** Whether the checklist was on screen last time, per person: lets the home page reserve its place before the request answers. */
const HINT = 'xc.help.checklist';

function applyPatch(s: HelpState, p: HelpPatch): HelpState {
  const add = (list: string[], v: string | undefined) => (v && !list.includes(v) ? [...list, v] : list);
  return {
    ...s,
    onboarding: { ...s.onboarding, dismissed: p.dismissed ?? s.onboarding.dismissed, stepsDone: add(s.onboarding.stepsDone, p.stepDone) },
    coachSeen: p.coachReset ? [] : add(s.coachSeen, p.coachSeen),
    whatsNewSeen: p.whatsNewSeen && whatsNewSeq(p.whatsNewSeen) > whatsNewSeq(s.whatsNewSeen) ? p.whatsNewSeen : s.whatsNewSeen,
  };
}

/** Change the person's own state: the screen updates at once, the server follows (a failure is retried by the next load). */
export function patchHelpState(patch: HelpPatch): void {
  updateSWR<HelpState>(KEY, (s) => applyPatch(s, patch));
  void api.patchHelp(patch).catch(() => invalidateSWR(KEY));
}

export function readChecklistHint(userId: string | undefined): boolean | null {
  try {
    const v = userId ? localStorage.getItem(`${HINT}.${userId}`) : null;
    return v === null ? null : v === '1';
  } catch {
    return null;
  }
}

export function writeChecklistHint(userId: string | undefined, shown: boolean): void {
  try {
    if (userId) localStorage.setItem(`${HINT}.${userId}`, shown ? '1' : '0');
  } catch {
    /* private mode: the place is simply not reserved next time */
  }
}

export interface HelpStateValue {
  state: HelpState | undefined;
  loading: boolean;
  patch: (p: HelpPatch) => void;
  reload: () => Promise<void>;
  /** A changelog entry newer than the last one this person saw. */
  whatsNewUnread: boolean;
}

export function useHelpState(): HelpStateValue {
  const session = useSession();
  const ws = session.status === 'authenticated' && !session.mustChangePassword ? session.workspace : null;
  // A minute of dedupe: every page change re-mounts readers, the answer rarely changes.
  const { data, loading, reload } = useSWR<HelpState>(ws ? `${KEY}${ws.id}` : null, () => api.helpState(ws!.id), { dedupeMs: 60_000 });
  const patch = useCallback((p: HelpPatch) => patchHelpState(p), []);
  return { state: data, loading, patch, reload, whatsNewUnread: !!data && whatsNewUnread(data.whatsNewSeen) };
}
